import { getSupabaseClient } from '@/integrations/supabase/client';
import { Tab } from '@/types';
import {
  applyStockDelta,
  applyPromotionComponentsStockDelta,
} from '@/services/products/productInventoryService';
import { productIngredientsService } from '@/services/products/productIngredientsService';
import {
  TAB_ITEM_MODIFIERS_EMBED,
  mapTabItemModifierRows,
  tabItemModifiersService,
} from '@/services/products/tabItemModifiersService';
import { dispatchReprintService } from '@/services/invoice/dispatchReprintService';
import { useDispatchReprintsStore } from '@/stores/dispatchReprintsStore';
import { parseTimestamp } from '@/utils/dateFormat';
import { sendEmail } from '@/services/sendEmail/sendEmailService';

const mapTabRowToTab = (tab: any): Tab => ({
  id: tab.id,
  customerName: tab.customer_name,
  discount: Number(tab.discount || 0),
  isOpen: tab.is_open ?? true,
  isCredit: tab.is_credit ?? false,
  isQuickSale: typeof tab.is_quick_sale === 'boolean' ? tab.is_quick_sale : undefined,
  createdAt: new Date(tab.created_at),
  updatedAt: new Date(tab.updated_at),
  createdBy: tab.created_by,
  organizationId: tab.organization_id,
  tableId: tab.table_id ?? null,
  ivaDisabled: tab.iva_disabled || false,
  serviceChargeDisabled: tab.service_charge_disabled ?? false,
  partySize: tab.party_size || undefined,
  serverName: tab.server_name || undefined,
  items: [],
});

const syncTableAssignmentAtomic = async (tableId: string, tabIds: string[]) => {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc('assign_tabs_to_table_atomic', {
    p_table_id: tableId,
    p_tab_ids: tabIds,
  });
  if (error) throw error;
};

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const syncTableAssignmentAtomicWithRetry = async (
  tableId: string,
  tabIds: string[],
  retries = 3
) => {
  let lastError: unknown;
  for (let attempt = 0; attempt < retries; attempt += 1) {
    try {
      await syncTableAssignmentAtomic(tableId, tabIds);
      return;
    } catch (error) {
      lastError = error;
      if (attempt < retries - 1) {
        await sleep(150 * 2 ** attempt);
      }
    }
  }
  throw lastError;
};

export const tabsService = {
  async getTabs(organizationId: string) {
    const supabase = getSupabaseClient();
    const { data: tabsData, error: tabsError } = await supabase
      .from('tabs')
      .select(`
        *,
        tab_items!left (
          id,
          product_id,
          product_name,
          product_category,
          category_id,
          category_id!left (
            id,
            name,
            print_in_kitchen,
            print_in_bar,
            organization_id,
            cabys_code,
            cabys_tax,
            activity_code
          ),
          quantity,
          unit_price,
          discount,
          notes,
          customer_position,
          weight_kg,
          created_at,
          products!left (
            stock,
            avoid_tracking,
            category_id,
            is_sold_by_weight
          ),
          tab_item_promotion_components!left (
            id,
            tab_item_id,
            component_product_id,
            quantity,
            product_name,
            created_at
          ),
          ${TAB_ITEM_MODIFIERS_EMBED}
        )
      `)
      .eq('organization_id', organizationId)
      .eq('is_open', true)
      .or('source_type.is.null,source_type.neq.external')      
      .order('created_at', { ascending: false });

    if (tabsError) throw tabsError;

    const processedTabs = tabsData.map(tab => {
      const tabItems = tab.tab_items || [];
      const sortedItems = tabItems
        .filter(item => item != null)
        .sort((a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

      return {
        id: tab.id,
        customerName: tab.customer_name,
        discount: Number(tab.discount || 0),
        isOpen: tab.is_open || false,
        isCredit: tab.is_credit || false,
        isQuickSale: typeof tab.is_quick_sale === 'boolean' ? tab.is_quick_sale : undefined,
        createdAt: new Date(tab.created_at),
        updatedAt: new Date(tab.updated_at),
        createdBy: tab.created_by,
        organizationId: tab.organization_id,
        tableId: tab.table_id ?? null,
        ivaDisabled: tab.iva_disabled || false,
        serviceChargeDisabled: tab.service_charge_disabled ?? false,
        partySize: tab.party_size || undefined,
        serverName: tab.server_name || undefined,
        items: sortedItems.map((item: any) => {
          const rawComps = item.tab_item_promotion_components ?? [];
          const promotionComponents = rawComps
            .filter((c: any) => c != null)
            .sort((a: any, b: any) =>
              String(a.product_name ?? '').localeCompare(String(b.product_name ?? '')) ||
              String(a.id ?? '').localeCompare(String(b.id ?? ''))
            )
            .map((c: any) => ({
              id: c.id,
              tabItemId: c.tab_item_id,
              componentProductId: c.component_product_id,
              quantity: Number(c.quantity ?? 0),
              productName: c.product_name,
            }));

          return {
            id: item.id,
            productId: item.product_id,
            product: {
              id: item.product_id,
              name: item.product_name,
              category: item.product_category,
              categoryId: item.products?.category_id ?? item.category_id?.id ?? '',
              productCategory: item.category_id
                ? {
                    id: item.category_id?.id,
                    name: item.category_id?.name,
                    printInKitchen: item.category_id?.print_in_kitchen,
                    printInBar: item.category_id?.print_in_bar,
                    showInSales: true,
                    organizationId,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    cabysCode: item.category_id?.cabys_code,
                    cabysTax: item.category_id?.cabys_tax,
                    activityCode: item.category_id?.activity_code,
                  }
                : undefined,
              cost: 0,
              price: Number(item.unit_price),
              stock: Number(item.products?.stock || 0),
              avoidTracking: Boolean(item.products?.avoid_tracking),
              isSoldByWeight: Boolean(item.products?.is_sold_by_weight),
              organizationId,
            },
            quantity: item.quantity,
            unitPrice: Number(item.unit_price),
            discount: Number(item.discount || 0),
            notes: item.notes || undefined,
            customerPosition: item.customer_position ?? undefined,
            weightKg: item.weight_kg != null ? Number(item.weight_kg) : undefined,
            // tab_items.created_at — when this line was committed (not tabs.created_at)
            createdAt: parseTimestamp(item.created_at),
            promotionComponents: promotionComponents.length ? promotionComponents : undefined,
            modifiers: mapTabItemModifierRows(item.tab_item_modifiers),
          };
        })
      };
    }) as Tab[];

    return processedTabs;
  },

  /**
   * Creates a tab with a client-generated id.
   *
   * The id and creation timestamp come from the client so the operation is
   * replayable offline: an offline-created tab keeps its original creation time,
   * and a retried/replayed insert is a no-op (`ignoreDuplicates` on the primary
   * key) instead of a duplicate or a 23505 error.
   */
  async createTab({
    tabId,
    customerName,
    userId,
    organizationId,
    tableId,
    isQuickSale,
    createdAtIso,
    serverName,
    discount,
  }: {
    tabId: string;
    customerName: string;
    userId: string;
    organizationId: string;
    tableId?: string | null;
    isQuickSale?: boolean;
    createdAtIso?: string;
    serverName?: string;
    discount?: number;
  }) {
    const supabase = getSupabaseClient();
    const createdAt = createdAtIso ?? new Date().toISOString();
    const { error } = await supabase
      .from('tabs')
      .upsert([{
        id: tabId,
        customer_name: customerName.trim(),
        created_by: userId,
        is_credit: false,
        organization_id: organizationId,
        table_id: tableId ?? null,
        is_quick_sale: isQuickSale ?? false,
        created_at: createdAt,
        updated_at: createdAt,
        server_name: serverName?.trim() || null,
        discount: discount ?? 0,
      }], { onConflict: 'id', ignoreDuplicates: true });

    if (error) throw error;

    if (tableId) {
      const { data: openRows, error: openError } = await supabase
        .from('tabs')
        .select('id')
        .eq('table_id', tableId)
        .eq('is_open', true);
      if (openError) throw openError;
      const currentIds = (openRows ?? []).map((row) => row.id as string);
      await syncTableAssignmentAtomicWithRetry(tableId, currentIds);
    }

    // The inserted row is fully determined by the input; no read-back needed.
    return {
      id: tabId,
      customerName: customerName.trim(),
      discount: discount ?? 0,
      isOpen: true,
      isCredit: false,
      isQuickSale: isQuickSale ?? false,
      createdAt: new Date(createdAt),
      updatedAt: new Date(createdAt),
      createdBy: userId,
      organizationId,
      tableId: tableId ?? null,
      partySize: undefined,
      serverName: serverName?.trim() || undefined,
      items: [],
    } satisfies Tab;
  },

  async updateTab(tabId: string, updates: { discount?: number; isCredit?: boolean; customerName?: string; ivaDisabled?: boolean; serviceChargeDisabled?: boolean; partySize?: number; serverName?: string; tableId?: string | null }) {
    const supabase = getSupabaseClient();
    const { data: currentTab, error: currentError } = await supabase
      .from('tabs')
      .select('table_id')
      .eq('id', tabId)
      .single();

    if (currentError) throw currentError;

    const updateData: any = {
      updated_at: new Date().toISOString(),
    };

    if (updates.discount !== undefined) {
      updateData.discount = updates.discount;
    }
    if (updates.isCredit !== undefined) {
      updateData.is_credit = updates.isCredit;
    }
    const shouldUnassignFromTable = updates.isCredit === true;
    if (shouldUnassignFromTable) {
      updateData.table_id = null;
    }
    if (updates.customerName !== undefined) {
      updateData.customer_name = updates.customerName.trim();
    }
    if (updates.ivaDisabled !== undefined) {
      updateData.iva_disabled = updates.ivaDisabled;
    }
    if (updates.serviceChargeDisabled !== undefined) {
      updateData.service_charge_disabled = updates.serviceChargeDisabled;
    }
    if (updates.partySize !== undefined) {
      updateData.party_size = updates.partySize;
    }
    if (updates.serverName !== undefined) {
      updateData.server_name = updates.serverName;
    }
    if (updates.tableId !== undefined && !shouldUnassignFromTable) {
      updateData.table_id = updates.tableId;
    }

    const { data, error } = await supabase
      .from('tabs')
      .update(updateData)
      .eq('id', tabId)
      .select()
      .single();

    if (error) throw error;

    const nextTableId = shouldUnassignFromTable
      ? null
      : updates.tableId !== undefined
        ? updates.tableId
        : undefined;
    if (nextTableId !== undefined) {
      const prev = currentTab.table_id as string | null;
      if (prev && prev !== nextTableId) {
        const { data: prevRows, error: prevRowsError } = await supabase
          .from('tabs')
          .select('id')
          .eq('table_id', prev)
          .eq('is_open', true);
        if (prevRowsError) throw prevRowsError;
        await syncTableAssignmentAtomic(prev, (prevRows ?? []).map((row) => row.id as string));
      }
      if (nextTableId) {
        const { data: nextRows, error: nextRowsError } = await supabase
          .from('tabs')
          .select('id')
          .eq('table_id', nextTableId)
          .eq('is_open', true);
        if (nextRowsError) throw nextRowsError;
        await syncTableAssignmentAtomic(
          nextTableId,
          (nextRows ?? []).map((row) => row.id as string)
        );
      }
    }

    return data;
  },

  async closeTab(tabId: string) {
    const supabase = getSupabaseClient();

    const { data: tabBeforeClose, error: tabBeforeError } = await supabase
      .from('tabs')
      .select('table_id')
      .eq('id', tabId)
      .single();

    if (tabBeforeError) throw tabBeforeError;

    const { data: tabData, error: tabError } = await supabase
      .from('tabs')
      .select('table_id, customer_name, discount, organization_id, created_at')
      .eq('id', tabId)
      .single();

    if (tabError) throw tabError;

    // Get tab items to calculate totals
    const { data: tabItems, error: itemsError } = await supabase
      .from('tab_items')
      .select('quantity, unit_price, discount')
      .eq('tab_id', tabId);

    if (itemsError) {
      console.warn('Failed to get tab items for email calculation', itemsError);
    }

    const { data, error } = await supabase
      .from('tabs')
      .update({
        is_open: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', tabId)
      .select()
      .single();

    if (error) throw error;

    if (tabBeforeClose.table_id) {
      const { data: remainingRows, error: remainingError } = await supabase
        .from('tabs')
        .select('id')
        .eq('table_id', tabBeforeClose.table_id)
        .eq('is_open', true);
      if (remainingError) throw remainingError;
      await syncTableAssignmentAtomic(
        tabBeforeClose.table_id,
        (remainingRows ?? []).map((row) => row.id as string)
      );
    }

    void dispatchReprintService.deleteForTab(tabId).catch((error) => {
      console.warn('Failed to clean up dispatch reprints for tab', tabId, error);
    });
    useDispatchReprintsStore.getState().clearForTab(tabId);

    // Calculate totals
    const items = tabItems ?? [];
    const subtotal = items.reduce((sum, item) => sum + Number(item.quantity || 0) * Number(item.unit_price || 0), 0);
    const discountAmount = subtotal * (Number(tabData.discount || 0) / 100);
    const total = subtotal - discountAmount;

    void sendDiscountNotificationIfNeeded(tabData as any, { subtotal, discountAmount, total, items: items.length }).catch((error) => {
      console.warn('Failed to send discount notification for tab', tabId, error);
    });

    return data;
  },

  async updateTabItemQuantity(itemId: string, quantity: number) {
    const supabase = getSupabaseClient();

    if (quantity <= 0) {
      // Delete the item if quantity is zero or negative
      const { error } = await supabase
        .from('tab_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
      return null;
    }

    const { data, error } = await supabase
      .from('tab_items')
      .update({ quantity })
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateTabItemDiscount(itemId: string, discount: number) {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
      .from('tab_items')
      .update({ discount })
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateTabItemNotes(itemId: string, notes: string | null) {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
      .from('tab_items')
      .update({ notes: notes || null })
      .eq('id', itemId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async incrementTabItemQuantity(itemId: string, incrementBy: number = 1) {
    const supabase = getSupabaseClient();

    // First get the current quantity
    const { data: currentItem, error: fetchError } = await supabase
      .from('tab_items')
      .select('quantity')
      .eq('id', itemId)
      .single();

    if (fetchError) throw fetchError;

    const newQuantity = currentItem.quantity + incrementBy;

    const { data, error } = await supabase
      .from('tab_items')
      .update({ quantity: newQuantity })
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteTab(tabId: string) {
    const supabase = getSupabaseClient();

    const { data: tabRow, error: tabError } = await supabase
      .from('tabs')
      .select('table_id')
      .eq('id', tabId)
      .single();

    if (tabError) throw tabError;

    const { data: tabItems, error: fetchError } = await supabase
      .from('tab_items')
      .select(
        `
        id,
        product_id,
        quantity,
        weight_kg,
        products(is_composite),
        tab_item_promotion_components (
          component_product_id,
          quantity
        ),
        tab_item_modifiers (
          modifier_id
        )
      `
      )
      .eq('tab_id', tabId);

    if (fetchError) throw fetchError;

    // Return stock for each product (promo lines: component products only)
    if (tabItems && tabItems.length > 0) {
      for (const item of tabItems) {
        try {
          const comps = (item as {
            tab_item_promotion_components?: { component_product_id: string; quantity: number }[];
          }).tab_item_promotion_components;
          if (comps && comps.length > 0) {
            await applyPromotionComponentsStockDelta(
              comps.map((row) => ({
                componentProductId: row.component_product_id,
                quantityPerBundle: Number(row.quantity),
              })),
              Number(item.quantity)
            );
          } else {
            const isComposite = (item.products as { is_composite?: boolean } | null)?.is_composite ?? false;
            const recipe = isComposite
              ? await productIngredientsService.getByProduct(item.product_id)
              : [];
            const weightKg = item.weight_kg != null ? Number(item.weight_kg) : null;
            const units =
              weightKg != null && weightKg > 0 ? weightKg : Number(item.quantity);
            await applyStockDelta(item.product_id, recipe, units, isComposite);
            await tabItemModifiersService.applyStockDelta(
              ((item as { tab_item_modifiers?: { modifier_id: string | null }[] }).tab_item_modifiers ?? [])
                .map((row) => row.modifier_id),
              Number(item.quantity)
            );
          }
        } catch (stockError) {
          console.error('Error returning stock for product:', item.product_id, stockError);
        }
      }
    }

    // One RPC: skip item-deletion logs, remove items, delete tab, write tab_deletions_history.
    const { error } = await supabase.rpc('delete_tab_with_audit', { p_tab_id: tabId });
    if (error) throw error;

    if (tabRow.table_id) {
      const { data: remainingRows, error: remainingError } = await supabase
        .from('tabs')
        .select('id')
        .eq('table_id', tabRow.table_id)
        .eq('is_open', true);
      if (remainingError) throw remainingError;
      await syncTableAssignmentAtomic(
        tabRow.table_id,
        (remainingRows ?? []).map((row) => row.id as string)
      );
    }

    return { success: true };
  },

  async getOpenTabsByTable(tableId: string): Promise<Tab[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('tabs')
      .select('*')
      .eq('table_id', tableId)
      .eq('is_open', true)
      .order('created_at', { ascending: true });

    if (error) throw error;

    return (data ?? []).map((row) => mapTabRowToTab(row));
  },

  async assignTabsToTable(tableId: string, tabIds: string[]): Promise<void> {
    const supabase = getSupabaseClient();
    await syncTableAssignmentAtomic(tableId, [...new Set(tabIds)]);
  }
};

/**
 * Send discount notification email if threshold exceeded and client is frequent
 */
const sendDiscountNotificationIfNeeded = async (tabData: any, totals: { subtotal: number; discountAmount: number; total: number; items: number }) => {
  const supabase = getSupabaseClient();
  const discountPercent = Number(tabData.discount || 0);
  const threshold = 30; // fixed threshold

  // Check if discount exceeds threshold
  if (discountPercent <= threshold) {
    return;
  }

  // Get all verified admin emails for the organization
  const adminEmails = await supabase.rpc('get_verified_cash_balance_admin_emails', {
    p_organization_id: tabData.organization_id
  });

  if (!adminEmails.data || adminEmails.data.length === 0) {
    return;
  }

  // Send email notification using simple email service with plain text
  try {
    await sendEmail({
      recipientEmails: adminEmails.data,
      subject: `Descuento superior al ${threshold}% para ${tabData.customer_name}`,
      textContent: `
        Se ha cerrado una cuenta al cliente ${tabData.customer_name} con un descuento de ${discountPercent}%.
        
        Subtotal: ${totals.subtotal.toLocaleString('es-CR', { style: 'currency', currency: 'CRC' })}
        Descuento (${discountPercent}%): -${totals.discountAmount.toLocaleString('es-CR', { style: 'currency', currency: 'CRC' })}
        Total a pagar: ${totals.total.toLocaleString('es-CR', { style: 'currency', currency: 'CRC' })}
      `,
    });
  } catch (error) {
    console.warn('Failed to send discount notification email', error);
  }
};
